import React, { useState, useEffect } from "react";
import {
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Container,
    Row,
    Col,
    FormGroup,
    Input,
    Label,
    TabContent,
    TabPane,
    Nav,
    NavItem,
    NavLink,
    Table,
    Badge,
    Button,
    Pagination,
    PaginationItem,
    PaginationLink,
    Modal,
    ModalHeader,
    ModalBody
} from "reactstrap";
import classnames from "classnames";
import CommonHeader from "components/Headers/CommonHeader";
import Swal from "sweetalert2";
import { getModerationQueue, updateModerationStatus, blockUserAPI, getAiFlags, getEscalatedQueue, resolveEscalationAPI, warnUser, getUserReports, createAdmin, getAllRooms } from "api/ApiServices";
import "assets/css/moderation.css";
import FlaggedTable from "./components/FlaggedTable";
import UserReportsTable from "./components/UserReportsTable";
import AiFlagsTable from "./components/AiFlagsTable";
import EscalatedTable from "./components/EscalatedTable";
import ReviewDrawer from "./components/ReviewDrawer";
import AssignedRoomsTable from "./components/AssignedRoomsTable";

const Moderation = () => {
    const role = localStorage.getItem("userRole");
    const roleLower = role?.toLowerCase() || "";
    const isAdmin = roleLower === "admin" || roleLower === "super admin" || roleLower === "super_admin";
    const isSeniorMod = role === "sen. moderator" || role === "senior_moderator" || role === "Senior Moderator" || roleLower === "senior moderator";

    const [activeTab, setActiveTab] = useState(() => {
        if (!isAdmin && !isSeniorMod) {
            return "assigned_rooms";
        }
        const stored = localStorage.getItem("mod_tab");
        if (stored) {
            localStorage.removeItem("mod_tab");
            return stored;
        }
        return "flagged";
    });
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const limit = 10;

    const [selectedStatus, setSelectedStatus] = useState("all");
    const [selectedCategory, setSelectedCategory] = useState("all");

    const handleCategoryChange = (e) => {
        setSelectedCategory(e.target.value);
        setPage(1);
    };

    const handleStatusChange = (e) => {
        setSelectedStatus(e.target.value);
        setPage(1);
    };

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [roomActivityModal, setRoomActivityModal] = useState({ open: false, room: null });

    const handleViewActivity = (room) => {
        setRoomActivityModal({ open: true, room });
    };

    const closeRoomActivityModal = () => {
        setRoomActivityModal({ open: false, room: null });
    };

    useEffect(() => {
        fetchData(activeTab, page, selectedStatus, selectedCategory);
    }, [activeTab, page, selectedStatus, selectedCategory]);

    /* ═══════════════════════════════════════════════════
       SMART PAGINATION RENDERER
       ═══════════════════════════════════════════════════ */
    const renderPaginationItems = (currentPage, totalItems, limit, onPageChange) => {
        const totalPages = Math.ceil(totalItems / limit);
        if (totalPages <= 1) return null;

        const pages = [];
        const delta = 2;

        pages.push(
            <PaginationItem key={1} active={currentPage === 1}>
                <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(1); }}>1</PaginationLink>
            </PaginationItem>
        );

        if (currentPage > delta + 2) {
            pages.push(<PaginationItem key="start-ellipsis" disabled><PaginationLink href="#pablo" onClick={(e) => e.preventDefault()}>...</PaginationLink></PaginationItem>);
        }

        const rangeStart = Math.max(2, currentPage - delta);
        const rangeEnd = Math.min(totalPages - 1, currentPage + delta);

        for (let i = rangeStart; i <= rangeEnd; i++) {
            pages.push(
                <PaginationItem key={i} active={currentPage === i}>
                    <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(i); }}>{i}</PaginationLink>
                </PaginationItem>
            );
        }

        if (currentPage < totalPages - delta - 1) {
            pages.push(<PaginationItem key="end-ellipsis" disabled><PaginationLink href="#pablo" onClick={(e) => e.preventDefault()}>...</PaginationLink></PaginationItem>);
        }

        if (totalPages > 1) {
            pages.push(
                <PaginationItem key={totalPages} active={currentPage === totalPages}>
                    <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(totalPages); }}>{totalPages}</PaginationLink>
                </PaginationItem>
            );
        }

        return pages;
    };

    const fetchData = async (tab, currentPage = 1, status = selectedStatus, category = selectedCategory) => {

        setLoading(true);

        try {

            const role = localStorage.getItem("userRole");
            const roleLower = role?.toLowerCase() || "";
            const isAdmin = roleLower === "admin" || roleLower === "super admin" || roleLower === "super_admin";

            // =====================================================
            // ASSIGNED ROOMS TAB
            // =====================================================
            if (tab === "assigned_rooms") {
                const queryParams = {
                    page: currentPage,
                    limit: 10
                };
                if (!isAdmin) {
                    queryParams.hasModerator = "true";
                }
                const res = await getAllRooms(queryParams);

                if ((res?.status === true || res?.status === "success") && res?.payload) {
                    setData(res.payload.rooms || []);
                    setTotalPages(res.payload.totalPages || 1);
                    setTotalItems(res.payload.total || 0);
                } else {
                    setData([]);
                    setTotalPages(1);
                    setTotalItems(0);
                }
            }

            // =====================================================
            // ESCALATED TAB
            // =====================================================
            else if (tab === "escalated") {
                const res = await getEscalatedQueue(
                    currentPage,
                    10,
                    status === "all" ? "all" : status
                );

                if (res?.status && res?.payload) {
                    const escalatedOnly = (res.payload.data || []).filter(
                        item => item?.status?.toUpperCase() === "ESCALATED" || item?.eh_status?.toUpperCase() === "PENDING"
                    );

                    const uniqueMap = new Map();
                    escalatedOnly.forEach(item => {
                        uniqueMap.set(item.id, item);
                    });
                    const uniqueEscalated = Array.from(uniqueMap.values());

                    setData(uniqueEscalated);
                    setTotalPages(res.payload.pagination?.totalPages || 1);
                    setTotalItems(uniqueEscalated.length);
                } else {
                    setData([]);
                    setTotalPages(1);
                    setTotalItems(0);
                }
            }

            // =====================================================
            // FLAGGED / USER REPORTS / AI FLAGS TAB (Unified Filtering)
            // =====================================================
            else if (tab === "flagged" || tab === "user" || tab === "ai") {
                if (
                    role === "sen. moderator" ||
                    role === "senior_moderator" ||
                    role === "Senior Moderator" ||
                    roleLower === "senior moderator"
                ) {
                    const res = await getEscalatedQueue(
                        currentPage,
                        10,
                        "all"
                    );

                    if (res?.status && res?.payload) {
                        const escalatedOnly = (res.payload.data || []).filter(
                            item => item?.status?.toUpperCase() === "ESCALATED"
                        );

                        const uniqueMap = new Map();
                        escalatedOnly.forEach(item => {
                            uniqueMap.set(item.id, item);
                        });
                        const uniqueEscalated = Array.from(uniqueMap.values());

                        setData(uniqueEscalated);
                        setTotalPages(res.payload.pagination?.totalPages || 1);
                        setTotalItems(uniqueEscalated.length);
                    } else {
                        setData([]);
                        setTotalPages(1);
                        setTotalItems(0);
                    }
                } else {
                    let queryType = "all";
                    if (tab === "user") {
                        queryType = "User Report";
                    } else if (tab === "ai") {
                        queryType = "AI Flag";
                    }

                    let queryStatus = status;
                    if (!isAdmin) {
                        queryStatus = "pending";
                    }

                    const res = await getModerationQueue(
                        currentPage,
                        10,
                        queryType,
                        queryStatus,
                        category === "all" ? "" : category
                    );

                    if (res?.status && res?.payload) {
                        const isEnriched = !Array.isArray(res.payload) && res.payload.data;
                        let finalData = isEnriched ? res.payload.data : res.payload;
                        finalData = finalData || [];

                        setData(finalData);
                        setTotalPages(
                            isEnriched
                                ? (res.payload.pagination?.totalPages || 1)
                                : 1
                        );
                        setTotalItems(
                            isEnriched
                                ? (res.payload.pagination?.totalItems || 0)
                                : (finalData?.length || 0)
                        );
                    } else {
                        setData([]);
                        setTotalPages(1);
                        setTotalItems(0);
                    }
                }
            }

            // =====================================================
            // UNKNOWN TAB
            // =====================================================
            else {
                console.log("Unknown tab =>", tab);
                setData([]);
                setTotalPages(1);
                setTotalItems(0);
            }

        } catch (error) {
            console.error("Error fetching moderation data =>", error);
            setData([]);
            setTotalPages(1);
            setTotalItems(0);
        } finally {


            setLoading(false);
        }
    };
    const toggleTab = (tab) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
            setPage(1);
            setIsDrawerOpen(false);
        }
    };

    const openReview = (item) => {
        setSelectedItem(item);
        setIsDrawerOpen(true);
    };

    const handleAction = async (newStatus, explicitItem = null) => {
        const itemToProcess = explicitItem || selectedItem;
        if (!itemToProcess) return;

        let confirmResult;
        const targetId = itemToProcess.id || itemToProcess._id;
        const targetType = itemToProcess.type || (activeTab === "user" ? "User Report" : "AI Flag");

        if (newStatus === "blocked") {
            confirmResult = await Swal.fire({
                title: "Block User?",
                text: "This will permanently restrict this user's access.",
                icon: "error",
                input: "text",
                inputPlaceholder: "Reason for blocking...",
                showCancelButton: true,
                confirmButtonColor: "#f5365c",
                confirmButtonText: "Yes, Block User",
                inputValidator: (value) => !value && 'A reason is required to block a user!'
            });
        } else if (newStatus === "unblocked") {
            confirmResult = await Swal.fire({
                title: "Unblock User?",
                text: "Are you sure you want to restore this user's access?",
                icon: "info",
                input: "text",
                inputPlaceholder: "Reason for unblocking... (optional)",
                showCancelButton: true,
                confirmButtonColor: "#11cdef",
                confirmButtonText: "Yes, Unblock User"
            });
        } else if (newStatus === "escalated") {
            confirmResult = await Swal.fire({
                title: "Escalate for Senior Review?",
                text: "Please provide a brief reason for this escalation:",
                input: "textarea",
                inputPlaceholder: "Reason for escalation...",
                showCancelButton: true,
                confirmButtonColor: "#5e72e4",
                confirmButtonText: "Escalate Now",
                inputValidator: (value) => !value && 'You must provide a reason for escalation!'
            });
        } else if (newStatus === "unescalated") {
            confirmResult = await Swal.fire({
                title: "Cancel Escalation?",
                text: "Are you sure you want to withdraw this item from senior review?",
                icon: "info",
                showCancelButton: true,
                confirmButtonColor: "#5e72e4",
                confirmButtonText: "Yes, Unescalate"
            });
        } else if (newStatus === "unwarned") {
            confirmResult = await Swal.fire({
                title: "Revoke Warning?",
                text: "Are you sure you want to revoke the warning applied to this user?",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#fb6340",
                confirmButtonText: "Yes, Revoke Warning"
            });
        } else {
            confirmResult = await Swal.fire({
                title: `Mark as ${newStatus}?`,
                text: `Are you sure you want to mark this item as ${newStatus}?`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#5e72e4",
                confirmButtonText: "Confirm"
            });
        }

        if (confirmResult.isConfirmed) {
            try {
                let success = false;
                if (itemToProcess.escalationId) {
                    // SENIOR RESOLUTION FLOW
                    const res = await resolveEscalationAPI({
                        escalationId: itemToProcess.escalationId,
                        status: newStatus,
                        notes: confirmResult.value || "Resolved by Senior Moderator"
                    });
                    if (res.status) success = true;
                } else if (newStatus === "blocked" || newStatus === "unblocked") {
                    const blockTargetId = itemToProcess.senderId || itemToProcess.userId || itemToProcess.reported_user_id || itemToProcess.user;
                    const res = await blockUserAPI(blockTargetId, confirmResult.value, newStatus);
                    if (res.status) {
                        const payload = { id: targetId, type: targetType, status: newStatus };
                        const modRes = await updateModerationStatus(payload);
                        if (modRes.status) success = true;
                    }
                } else if (newStatus === "warned") {
                    const warnTargetId = itemToProcess.senderId || itemToProcess.userId || itemToProcess.user?._id || itemToProcess.user;
                    await warnUser({ userId: warnTargetId });

                    const payload = { id: targetId, type: targetType, status: newStatus };
                    const res = await updateModerationStatus(payload);
                    if (res.status) success = true;
                } else {
                    const payload = { id: targetId, type: targetType, status: newStatus };
                    if (newStatus === "escalated" && confirmResult.value) {
                        payload.notes = confirmResult.value;
                    }
                    const res = await updateModerationStatus(payload);
                    if (res.status) success = true;
                }

                if (success) {
                    Swal.fire("Success!", `Action performed successfully.`, "success");
                    if (newStatus === "blocked" || newStatus === "unblocked") {
                        if (selectedItem && (selectedItem.id === itemToProcess.id)) {
                            setSelectedItem({
                                ...itemToProcess,
                                isUserBlocked: newStatus === "blocked",
                                status: newStatus
                            });
                        }
                        fetchData(activeTab, page);
                    } else {
                        setIsDrawerOpen(false);
                        fetchData(activeTab, page);
                    }
                } else {
                    Swal.fire("Error", "Action could not be completed.", "error");
                }
            } catch (error) {
                console.error("Moderation action error:", error);
                const errorMsg = error.response?.data?.message || "Could not complete action.";
                Swal.fire("Error", errorMsg, "error");
            }

        }
    };

    const renderTable = () => {
        if (loading) {
            return (
                <div className="text-center py-5">
                    <i className="fas fa-spinner fa-spin fa-2x text-primary" />
                    <p className="mt-3">Loading Queue...</p>
                </div>
            );
        }

        if (data.length === 0) {
            let icon = "ni ni-check-bold text-success";
            let title = "Queue is empty";
            let subtitle = "No moderation items pending here!";

            if (activeTab === "user") {
                icon = "ni ni-folder-17 text-info";
                title = "No user reports available";
                subtitle = "";
            } else if (activeTab === "flagged") {
                icon = "ni ni-bell-55 text-info";
                title = "No flagged content available";
                subtitle = "No content flagged by AI for review at this time.";
            } else if (activeTab === "assigned_rooms") {
                icon = "ni ni-chat-round text-info";
                title = "No assigned rooms found";
                subtitle = "There are currently no chat rooms assigned to moderators.";
            }

            return (
                <div className="text-center py-5 text-muted">
                    <i className={`${icon} display-2 mb-3`}></i>
                    <h4>{title}</h4>
                    {subtitle && <p>{subtitle}</p>}
                </div>
            );
        }

        switch (activeTab) {
            case "ai":
                return <AiFlagsTable data={data} />;
            case "escalated":
                return <EscalatedTable data={data} />;
            case "user":
                return <UserReportsTable data={data} onReview={openReview} />;
            case "assigned_rooms":
                return (
                    <AssignedRoomsTable
                        data={data}
                        onViewActivity={handleViewActivity}
                        onRefresh={() => fetchData(activeTab, page)}
                    />
                );
            default:
                return <FlaggedTable data={data} onReview={openReview} />;
        }
    };

    const getTitle = () => {
        if (activeTab === "user") return "User Reports Queue";
        if (activeTab === "ai") return "AI Flags";
        if (activeTab === "escalated") return "Escalation Audit Trail";
        if (activeTab === "assigned_rooms") return "Assigned Moderation Rooms";
        return "Flagged Content";
    };



    return (
        <>
            <CommonHeader />
            <Container className="mt--7" fluid>
                <Row>
                    <div className="col">
                        <Card className="shadow moderation-card">
                            <CardHeader className="bg-transparent border-0 pb-0">
                                <Row className="align-items-center mb-3">
                                    <div className="col">
                                        <h3 className="mb-0 text-uppercase text-muted font-weight-800" style={{ letterSpacing: '2px' }}>
                                            <i className="ni ni-settings text-red mr-2" /> Moderation Center
                                        </h3>
                                    </div>
                                </Row>

                                {/* TABS */}
                                <Nav pills className="flex-column flex-sm-row">
                                    <NavItem className="mr-sm-2 mb-sm-0 mb-2">
                                        <NavLink
                                            className={classnames({
                                                active: activeTab === "flagged",
                                            })}
                                            onClick={() => toggleTab("flagged")}
                                            style={{ cursor: "pointer", fontWeight: "600", fontSize: "13px", padding: "8px 16px", borderRadius: "8px" }}
                                        >
                                            <i className="ni ni-bell-55 mr-2"></i>
                                            Flagged Content
                                        </NavLink>
                                    </NavItem>
                                    <NavItem className="mr-sm-2 mb-sm-0 mb-2">
                                        <NavLink
                                            className={classnames({
                                                active: activeTab === "user",
                                            })}
                                            onClick={() => toggleTab("user")}
                                            style={{ cursor: "pointer", fontWeight: "600", fontSize: "13px", padding: "8px 16px", borderRadius: "8px" }}
                                        >
                                            <i className="ni ni-support-16 mr-2"></i>
                                            User Reports
                                        </NavLink>
                                    </NavItem>
                                    {isAdmin && (
                                        <NavItem className="mr-sm-2 mb-sm-0 mb-2">
                                            <NavLink
                                                className={classnames({
                                                    active: activeTab === "ai",
                                                })}
                                                onClick={() => toggleTab("ai")}
                                                style={{ cursor: "pointer", fontWeight: "600", fontSize: "13px", padding: "8px 16px", borderRadius: "8px" }}
                                            >
                                                <i className="ni ni-sound-wave mr-2"></i>
                                                AI Flags
                                            </NavLink>
                                        </NavItem>
                                    )}
                                    <NavItem className="mb-sm-0 mb-2">
                                        <NavLink
                                            className={classnames({
                                                active: activeTab === "assigned_rooms",
                                            })}
                                            onClick={() => toggleTab("assigned_rooms")}
                                            style={{ cursor: "pointer", fontWeight: "600", fontSize: "13px", padding: "8px 16px", borderRadius: "8px" }}
                                        >
                                            <i className="ni ni-chat-round mr-2"></i>
                                            Moderation Rooms
                                        </NavLink>
                                    </NavItem>
                                </Nav>

                                {/* FILTERS BAR */}
                                {(activeTab === "flagged" || activeTab === "user" || activeTab === "ai") && (
                                    <Row className="mt-4 mb-2 align-items-center">
                                        <Col md="4" sm="6" className="mb-md-0 mb-3">
                                            <FormGroup className="mb-0">
                                                <Label className="text-muted font-weight-bold text-xs text-uppercase mb-1 d-block" style={{ letterSpacing: '1px' }}>
                                                    <i className="ni ni-tag text-primary mr-1" /> Filter by Category
                                                </Label>
                                                <Input
                                                    type="select"
                                                    value={selectedCategory}
                                                    onChange={handleCategoryChange}
                                                    style={{
                                                        borderRadius: "8px",
                                                        border: "1px solid #e2e8f0",
                                                        padding: "10px 14px",
                                                        fontSize: "14px",
                                                        fontWeight: "600",
                                                        color: "#4a5568",
                                                        boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                                                        transition: "all 0.2s ease"
                                                    }}
                                                >
                                                    <option value="all">All Categories</option>
                                                    <option value="Toxicity">Toxicity</option>
                                                    <option value="Spam">Spam</option>
                                                    <option value="Harassment">Harassment</option>
                                                    <option value="Sexual">Sexual</option>
                                                    <option value="Identity Hate">Identity Hate</option>
                                                    <option value="Threat">Threat</option>
                                                    <option value="Insult">Insult</option>
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        <Col md="4" sm="6" className="mb-md-0 mb-3">
                                            <FormGroup className="mb-0">
                                                <Label className="text-muted font-weight-bold text-xs text-uppercase mb-1 d-block" style={{ letterSpacing: '1px' }}>
                                                    <i className="ni ni-bullet-list-67 text-primary mr-1" /> Filter by Status
                                                </Label>
                                                <Input
                                                    type="select"
                                                    value={selectedStatus}
                                                    onChange={handleStatusChange}
                                                    style={{
                                                        borderRadius: "8px",
                                                        border: "1px solid #e2e8f0",
                                                        padding: "10px 14px",
                                                        fontSize: "14px",
                                                        fontWeight: "600",
                                                        color: "#4a5568",
                                                        boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                                                        transition: "all 0.2s ease"
                                                    }}
                                                >
                                                    <option value="all">All Statuses</option>
                                                    <option value="pending">Pending</option>
                                                    <option value="approved">Approved</option>
                                                    <option value="removed">Removed</option>
                                                    <option value="escalated">Escalated</option>
                                                    <option value="warned">Warned</option>
                                                    <option value="blocked">Blocked</option>
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                )}
                            </CardHeader>

                            <CardHeader className="border-0 pt-4">
                                <h3 className="mb-0 font-weight-bold">{getTitle()}</h3>
                            </CardHeader>

                            <TabContent activeTab={activeTab}>
                                <TabPane tabId="ai">{renderTable()}</TabPane>
                                <TabPane tabId="flagged">{renderTable()}</TabPane>
                                <TabPane tabId="user">{renderTable()}</TabPane>
                                <TabPane tabId="escalated">{renderTable()}</TabPane>
                                <TabPane tabId="assigned_rooms">{renderTable()}</TabPane>
                            </TabContent>

                            {activeTab !== "ai" && (
                                <CardFooter className="py-4 border-0 bg-transparent d-flex justify-content-between align-items-center">
                                    <span className="text-muted small">
                                        Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, totalItems)} of {totalItems}
                                    </span>
                                    <nav aria-label="...">
                                        <Pagination className="pagination justify-content-end mb-0" listClassName="justify-content-end mb-0">
                                            <PaginationItem disabled={page <= 1}>
                                                <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); setPage(page - 1); }} tabIndex="-1">
                                                    <i className="fas fa-angle-left" />
                                                </PaginationLink>
                                            </PaginationItem>
                                            {renderPaginationItems(page, totalItems, limit, setPage)}
                                            <PaginationItem disabled={page >= totalPages}>
                                                <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); setPage(page + 1); }}>
                                                    <i className="fas fa-angle-right" />
                                                </PaginationLink>
                                            </PaginationItem>
                                        </Pagination>
                                    </nav>
                                </CardFooter>
                            )}
                        </Card>
                    </div>
                </Row>
            </Container>

            {/* ROOM ACTIVITY MODAL */}
            <Modal isOpen={roomActivityModal.open} toggle={closeRoomActivityModal} size="lg" centered>
                <ModalHeader toggle={closeRoomActivityModal} style={{ borderBottom: "1px solid #e9ecef" }}>
                    <i className="fas fa-eye mr-2 text-primary" />
                    Room Activity — {roomActivityModal.room?.name || roomActivityModal.room?.r_name || "Chat Room"}
                </ModalHeader>
                <ModalBody style={{ padding: "24px" }}>
                    {roomActivityModal.room && (() => {
                        const room = roomActivityModal.room;
                        const roomName = room.name || room.r_name || "Chat Room";
                        const location = room.city && room.city !== "N/A" ? `${room.city}, ${room.country}` : room.country || "Global";
                        const moderators = room.moderatorsInfo || [];
                        return (
                            <>
                                {/* Room Info */}
                                <div className="d-flex align-items-center mb-4 p-3" style={{ background: "#f8f9fe", borderRadius: "12px" }}>
                                    <div style={{ width: "52px", height: "52px", borderRadius: "12px", background: "linear-gradient(135deg, #5e72e4, #825ee4)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: "20px", flexShrink: 0 }} className="mr-3">
                                        {roomName.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <h4 className="mb-0 font-weight-bold text-dark">{roomName}</h4>
                                        <small className="text-muted"><i className="fas fa-map-marker-alt mr-1" />{location}</small>
                                    </div>
                                    <div className="ml-auto">
                                        <Badge color="" style={{ background: room.status === "Active" ? "#d4f5e9" : "#f1f3f4", color: room.status === "Active" ? "#1a7a52" : "#5a6370", padding: "6px 12px", borderRadius: "20px", fontSize: "12px", fontWeight: 600 }}>
                                            <span style={{ display: "inline-block", width: "7px", height: "7px", borderRadius: "50%", background: room.status === "Active" ? "#2dce89" : "#adb5bd", marginRight: "6px" }} />
                                            {room.status || "Unknown"}
                                        </Badge>
                                    </div>
                                </div>

                                {/* Stats Row */}
                                <div className="row mb-4">
                                    <div className="col-4">
                                        <div className="text-center p-3" style={{ background: "#eef2ff", borderRadius: "12px" }}>
                                            <div style={{ fontSize: "28px", fontWeight: 700, color: "#5e72e4" }}>{room.users || 0}</div>
                                            <small className="text-muted text-uppercase" style={{ fontSize: "10px", fontWeight: 600 }}>Users</small>
                                        </div>
                                    </div>
                                    <div className="col-4">
                                        <div className="text-center p-3" style={{ background: "#e8fdf3", borderRadius: "12px" }}>
                                            <div style={{ fontSize: "28px", fontWeight: 700, color: "#2dce89" }}>{room.messages || 0}</div>
                                            <small className="text-muted text-uppercase" style={{ fontSize: "10px", fontWeight: 600 }}>Messages</small>
                                        </div>
                                    </div>
                                    {isAdmin && (
                                    <div className="col-4">
                                        <div className="text-center p-3" style={{ background: "#fff4ec", borderRadius: "12px" }}>
                                            <div style={{ fontSize: "28px", fontWeight: 700, color: "#fb6340" }}>{moderators.length}</div>
                                            <small className="text-muted text-uppercase" style={{ fontSize: "10px", fontWeight: 600 }}>Moderators</small>
                                        </div>
                                    </div>
                                    )}
                                </div>

                                {/* Room Details */}
                                <div className="mb-4">
                                    <h6 className="text-uppercase text-muted ls-1 mb-2" style={{ fontSize: "11px", letterSpacing: "1px" }}>Room Details</h6>
                                    <div style={{ border: "1px solid #e9ecef", borderRadius: "10px", overflow: "hidden" }}>
                                        {(() => {
                                            const creatorName = room.userProfile?.name || room.userProfile?.handlername || "—";
                                            const creatorEmail = room.userProfile?.email || "";
                                            const createdAt = room.createdAt ? new Date(room.createdAt).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—";
                                            const items = [
                                                { label: "Room Type", value: room.type || room.r_type || "—" },
                                                { label: "Description", value: room.description || room.r_description || "No description provided" },
                                                { label: "Location", value: location },
                                                { label: "Visibility", value: room.visibility || room.r_visibility || "—" },
                                                { label: "Eligible", value: room.eligible === true ? "Yes" : room.eligible === false ? "No" : "—" },
                                                { label: "Created By", value: creatorName + (creatorEmail ? ` (${creatorEmail})` : "") },
                                                { label: "Created At", value: createdAt },
                                            ];
                                            return items.map((item, i) => (
                                                <div key={i} className="d-flex align-items-start" style={{ borderBottom: i < items.length - 1 ? "1px solid #f0f2f5" : "none", padding: "10px 16px" }}>
                                                    <span style={{ minWidth: "130px", color: "#8898aa", fontSize: "12px", fontWeight: 600, flexShrink: 0, paddingTop: "1px" }}>{item.label}</span>
                                                    <span style={{ color: "#32325d", fontSize: "13px", wordBreak: "break-word" }}>{item.value}</span>
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                </div>

                                {/* Assigned Moderators */}
                                {isAdmin && (
                                <div>
                                    <h6 className="text-uppercase text-muted ls-1 mb-2" style={{ fontSize: "11px", letterSpacing: "1px" }}>Assigned Moderators</h6>
                                    {moderators.length === 0 ? (
                                        <div className="text-center py-3" style={{ border: "1px dashed #dee2e6", borderRadius: "10px", color: "#fb6340" }}>
                                            <i className="fas fa-exclamation-triangle mr-2" />No moderator assigned to this room
                                        </div>
                                    ) : (
                                        <div style={{ border: "1px solid #e9ecef", borderRadius: "10px", overflow: "hidden" }}>
                                            {moderators.map((mod, i) => {
                                                const modName = `${mod.au_name} ${mod.au_surname || ""}`.trim();
                                                return (
                                                    <div key={i} className="d-flex align-items-center" style={{ padding: "10px 16px", borderBottom: i < moderators.length - 1 ? "1px solid #f0f2f5" : "none" }}>
                                                        <div style={{ width: "32px", height: "32px", borderRadius: "50%", background: "#11cdef", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", fontWeight: "bold", flexShrink: 0 }} className="mr-3">
                                                            {modName.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <div className="font-weight-bold text-dark" style={{ fontSize: "13px" }}>{modName}</div>
                                                            <small className="text-muted" style={{ fontSize: "11px" }}>{mod.au_email}</small>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                )}
                            </>
                        );
                    })()}
                </ModalBody>
            </Modal>

            {/* MESSAGE REVIEW DRAWER */}
            <ReviewDrawer
                isOpen={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                selectedItem={selectedItem}
                handleAction={handleAction}
                activeTab={activeTab}
                isSeniorMod={isSeniorMod}
            />
        </>
    );
};

export default Moderation;